import { FunctionComponent, ReactElement, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { Stack, useBreakpointValue, VStack } from '@chakra-ui/react';

import Button from '../../../../../components/_core/Button';
import { BeneficiarySchema } from '../beneficiarySchema';

interface BeneficiaryFormWrapperProps {
  children: (state: {
    isInvalidBeneficiary: boolean;
    isValidBeneficiary: boolean;
  }) => ReactNode;
  primaryAction: {
    title: string;
    onSubmit: (formValues: BeneficiarySchema) => void;
    isSending: boolean;
    isDisabled?: boolean;
  };
  secondaryAction: ReactElement;
}

const BeneficiaryFormWrapper: FunctionComponent<
  BeneficiaryFormWrapperProps
> = ({ children, primaryAction, secondaryAction }) => {
  const { handleSubmit, watch } = useFormContext<BeneficiarySchema>();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const [isDirector, isMajorShareholder] = watch([
    'isDirector',
    'isMajorShareholder',
  ]);

  const beneficiariesAreDefined =
    isDirector !== undefined && isMajorShareholder !== undefined;
  const isValidBeneficiary =
    beneficiariesAreDefined && (isDirector || isMajorShareholder);
  const isInvalidBeneficiary =
    isDirector === false && isMajorShareholder === false;

  return (
    <VStack
      align="stretch"
      as="form"
      height="full"
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit((formValues) => {
          if (isValidBeneficiary) {
            primaryAction.onSubmit(formValues);
          }
        })();
      }}
    >
      {children({ isInvalidBeneficiary, isValidBeneficiary })}
      <Stack
        alignContent="flex-end"
        direction={isMobile ? 'column' : 'row'}
        flexDirection={isMobile ? 'column-reverse' : 'row'}
        justifyContent="flex-end"
        marginTop="space-48"
        paddingBottom="space-48"
        spacing="space-16"
      >
        {secondaryAction}
        <Button
          isDisabled={primaryAction.isDisabled || isInvalidBeneficiary}
          isLoading={primaryAction.isSending}
          type="submit"
        >
          {primaryAction.title}
        </Button>
      </Stack>
    </VStack>
  );
};

export default BeneficiaryFormWrapper;
